/**
 * Created by Ning Jiang on 10/29/2019.
 */

import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {GameMode} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {TimelineLite, TweenLite} from "gsap";
import {DeadwoodGameConfig} from "../DeadwoodGameConfig";
import {DeadwoodSoundConfig} from "../DeadwoodSoundConfig";
import {DeadwoodGameMode} from "../gamemode/DeadwoodGameMode";
import {MultiplierPageContainer} from "./MultiplierPageContainer";

export class DeadwoodMultiplierView extends BaseView {
    private readonly _positions:[number, number] = [930, 110];

    private _container:PIXI.Container;
    private _currentPage:MultiplierPageContainer | null;
    private _pagePool:MultiplierPageContainer[] = [];
    private _daggerContainer:PIXI.Container;
    private _daggerSprite:PIXI.Sprite;
    private _daggerSpine:PIXI.spine.Spine;
    private _daggerHalf:PIXI.Sprite;
    private _fireContainer:PIXI.Container;
    private _closeFire:TimelineSprite;
    private _firePtSprite:TimelineSprite;

    constructor() {
        super();
    }

    protected initAnimations():void {
        const layer:ScalableLayer = StageManager.getLayer(DeadwoodGameConfig.instance.LAYERS.MULTIPLIER.name);
        this._container = new PIXI.Container();
        this._container.position.set(this._positions[0], this._positions[1]);
        this._container.visible = false;
        layer.addChild(this._container);

        this._daggerContainer = new PIXI.Container();
        this._daggerContainer.position.set(this._positions[0], this._positions[1]);
        this._daggerContainer.visible = false;
        layer.addChild(this._daggerContainer);

        this._fireContainer = new PIXI.Container();
        this._fireContainer.position.set(this._positions[0], this._positions[1]);
        layer.addChild(this._fireContainer);
        // for test.
        // this.showMultiplier(88, "", false);
    }

    public showMultiplier(number:number, mode:string, animate:boolean):void {
        if(!this._currentPage) {
            // show new page
            this._currentPage = this.getMultiplierPage();
            if(animate) {
                this._currentPage.updateMultiplier(number, mode === GameMode.NORMAL ? "normal_landing" : "freespin_landing");
                TweenLite.fromTo(this._currentPage, 0.2, {x : -20, y : -50, alpha : 0}, {x : 0, y : 0, alpha : 1});
                TweenLite.fromTo(this._currentPage, 0.15, {rotation : 0.15}, {rotation : 0, delay : 0.08});
                this._currentPage.popMultiplier(0.1);
            } else {
                this._currentPage.updateMultiplier(number, mode === GameMode.NORMAL ? "normal" : "freespin");
            }
            this._container.addChild(this._currentPage);
            this._container.alpha = 1;
            this._container.visible = true;
        } else {
            // update page.
            if(animate) {
                DeadwoodSoundConfig.playPaperSound(number);
                const tearPage:MultiplierPageContainer = this.getMultiplierPage();
                tearPage.updateMultiplier(number - 1, mode === GameMode.NORMAL ? "normal_drag" : (mode === DeadwoodGameMode.FREESPIN_GUNSLINGER ? "dagger_drag" : "freespin_drag"));
                tearPage.alpha = 1;
                TweenLite.fromTo(tearPage, 0.3, {x : 0, y : 0}, {x : 20, y : 70});
                TweenLite.fromTo(tearPage, 0.1, {rotation : 0}, {rotation : -0.2});
                TweenLite.fromTo(tearPage, 0.21,
                    {alpha : 1},
                    {
                        alpha : 0, delay : 0.1, onComplete : () => {
                            // recycle tearPage.
                            this._container.removeChild(tearPage);
                            this._pagePool.push(tearPage);
                        }
                    });

                this._container.addChild(tearPage);
                this._currentPage.popMultiplier(0.1);
            }
            this._currentPage.updateMultiplier(number, mode === GameMode.NORMAL ? "normal" : "freespin");
        }
    }

    public hideMultiplier(animate:boolean):void {
        if(!this._currentPage) {
            return;
        }

        if(!this._closeFire) {
            this._closeFire = new TimelineSprite(GameResources.getTextures("multiplierFire"));
            this._closeFire.anchor.set(0.5, 0.53);
            this._closeFire.visible = false;
            this._fireContainer.addChild(this._closeFire);

            this._firePtSprite = new TimelineSprite(GameResources.getTextures("redWildPt"));
            this._firePtSprite.blendMode = PIXI.BLEND_MODES.ADD;
            this._firePtSprite.anchor.set(0.5);
            this._firePtSprite.rotation = -Math.PI / 180 * 200;
            this._firePtSprite.visible = false;
            this._fireContainer.addChild(this._firePtSprite);
        }
        const timeline:TimelineLite = new TimelineLite();
        if(animate) {
            timeline.add([
                () => SlotGame.sound.playEffect(DeadwoodSoundConfig.instance.MULTIPLIER_FIRE),
                this._closeFire.getAnimationAutoShowHide(),
                this._firePtSprite.getAnimationAutoShowHide()
            ], 0.5);
        }
        timeline.add(TweenLite.to(this._container, 0.2, {
            alpha : 0, onComplete : () => {
                this._container.visible = false;
                this._container.removeChild(this._currentPage!);
                this._pagePool.push(this._currentPage!);
                this._currentPage = null;

                this.removeDagger();
            }
        }), animate ? 0.7 : 0);
    }

    private getMultiplierPage():MultiplierPageContainer {
        if(this._pagePool.length > 0) {
            return this._pagePool.pop()!
        } else {
            return new MultiplierPageContainer();
        }
    }

    public addDagger():void {
        if(!this._daggerSprite) {
            this._daggerHalf = new PIXI.Sprite(GameResources.getTextures("multiplierDaggerEnd")[0]);
            this._daggerHalf.position.set(-10, -110);
            this._daggerContainer.addChild(this._daggerHalf);

            this._daggerSprite = new PIXI.Sprite();
            this._daggerSprite.anchor.set(0.5);
            this._daggerSprite.position.set(85, -15);
            this._daggerContainer.addChild(this._daggerSprite);

            this._daggerSpine = new PIXI.spine.Spine(GameResources.getSpineAsset("multiplierDagger"));
            this._daggerSpine.autoUpdate = true;
            this._daggerSprite.addChild(this._daggerSpine);
        }
        this._daggerHalf.visible = false;
        this._daggerSpine.state.clearTrack(0);
        this._daggerSpine.skeleton.setToSetupPose();
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            new SpineTween(this._daggerSpine, 0, "animation"),
            () => {
                this._daggerContainer.visible = true;
                SlotGame.sound.playEffect(DeadwoodSoundConfig.instance.KNIFE);
            }
        ]);
        const containerTimeline:TimelineLite = new TimelineLite();
        containerTimeline.add([
            TweenLite.fromTo(this._container.scale, 0.01, {x : 1, y : 1}, {x : 1.1, y : 1.1}),
            TweenLite.fromTo(this._container, 0.01, {rotation : 0}, {rotation : -Math.PI / 180 * 14})
        ]);
        containerTimeline.add([
            TweenLite.to(this._container.scale, 0.1, {x : 1, y : 1}),
            TweenLite.to(this._container, 0.1, {rotation : 0})
        ]);
        containerTimeline.add(TweenLite.to(this._container.scale, 0.03, {x : 1.02, y : 1.02}));
        containerTimeline.add(TweenLite.to(this._container.scale, 0.1, {x : 1, y : 1}));
        timeline.add([
            () => this._daggerHalf.visible = true,
            containerTimeline
        ], "+=0.1");
    }

    public removeDagger():void {
        if(this._daggerSprite) {
            this._daggerContainer.visible = false;
            this._daggerSpine.state.setEmptyAnimation(0, 0);
            this._daggerHalf.visible = false;
        }
    }
}